import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { INativeBridge, PlatformInfo } from '@erp/native/interfaces';
import { createWebBridge } from '@erp/native/web-adapter';

const NativeBridgeContext = createContext<INativeBridge | null>(null);

/**
 * True at runtime when the bundle is being executed inside a Tauri webview.
 * This is the canonical detection — Tauri injects `__TAURI_INTERNALS__` on
 * `window` long before any user code runs.
 */
export function isRunningInTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

/**
 * Boots the right native bridge. Web is synchronous; Tauri requires an
 * async platform probe (OS name, version, locale) so we render a tiny
 * splash until ready.
 */
export function NativeBridgeProvider({ children }: { children: ReactNode }) {
  const [bridge, setBridge] = useState<INativeBridge | null>(() =>
    isRunningInTauri() ? null : createWebBridge(),
  );

  useEffect(() => {
    let cancelled = false;
    if (!isRunningInTauri() || bridge) return;
    // Dynamic import keeps Tauri-specific code out of the web bundle —
    // Vite tree-shakes the unused branch when building for Cloudflare.
    void import('@erp/native/tauri-adapter')
      .then(async (mod) => {
        const b = await mod.createTauriBridge();
        if (!cancelled) setBridge(b);
      })
      .catch((err) => {
        // Tauri bridge failed for some reason — fall back to the web
        // adapter so at least the app boots and we can show the error.
        console.error('[native] Tauri bridge failed, falling back to web', err);
        if (!cancelled) setBridge(createWebBridge());
      });
    return () => {
      cancelled = true;
    };
  }, [bridge]);

  if (!bridge) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Initialisation…
      </div>
    );
  }

  return (
    <NativeBridgeContext.Provider value={bridge}>{children}</NativeBridgeContext.Provider>
  );
}

export function useNativeBridge(): INativeBridge {
  const ctx = useContext(NativeBridgeContext);
  if (!ctx) throw new Error('useNativeBridge must be used within NativeBridgeProvider');
  return ctx;
}

export function usePlatform(): PlatformInfo {
  return useNativeBridge().platform;
}

/** Convenience hook — true if any desktop platform. */
export function useIsDesktop(): boolean {
  return useNativeBridge().platform.isDesktop;
}
